/**
 * Lao dictionary — the source of truth for UI copy.
 *
 * Ported from LANG.la in Seenaly Clinic.dc.html (docs/spec.md §8.1). `en.ts` is
 * typed against this object, so adding a key here and forgetting the English
 * translation is a compile error rather than a blank label in production.
 *
 * Deliberately NOT carried over from the prototype: `userName`, `userRole`,
 * `delta`, `services` and `activity`. Those were demo values, not translations —
 * they come from the session and the database (P2, P7).
 */
export const la = {
  clinicName: 'ຊີນາລີ ຄລີນິກ',
  clinicKicker: 'ລະບົບຄຸ້ມຄອງຄລີນິກ',
  clinicSubtitle: 'ຊ່ຽວຊານປະສູດພະຍາດຍີງ / ທົ່ວໄປ',
  clinicPhone: 'ໂທ: 020 28265455',

  breadcrumbRoot: 'ລະບົບຄລີນິກ',
  searchPlaceholder: 'ຄົ້ນຫາຄົນເຈັບ, ນັດໝາຍ, ໃບເກັບເງິນ...',
  langLabel: 'EN',

  menuMain: 'ເມນູຫຼັກ',
  menuReports: 'ລາຍງານ',
  reportRevenue: 'ລາຍງານລາຍຮັບ',
  reportPatients: 'ລາຍງານຄົນເຈັບ',

  helpTitle: 'ຕ້ອງການຄວາມຊ່ວຍເຫຼືອ?',
  // The prototype's English copy carried a different number (021 456 789).
  // 020 28265455 is the real one — it matches the clinic's printed lab report.
  helpBody: 'ຕິດຕໍ່ທີມສະໜັບສະໜູນ 020 28265455',

  greetingMorning: 'ສະບາຍດີຕອນເຊົ້າ',
  greetingAfternoon: 'ສະບາຍດີຕອນບ່າຍ',
  greetingEvening: 'ສະບາຍດີຕອນແລງ',
  greetingSub: 'ສະຫຼຸບການເຄື່ອນໄຫວຄລີນິກມື້ນີ້',

  refresh: 'ຣີເຟຣຊ',
  viewList: 'ລາຍການ',
  viewCalendar: 'ປະຕິທິນ',
  viewAll: 'ເບິ່ງທັງໝົດ',

  nav: {
    dashboard: 'ແດຊບອດ',
    appointments: 'ນັດໝາຍ',
    patients: 'ຄົນເຈັບ',
    billing: 'ໃບເກັບເງິນ',
    inventory: 'ຄັງຢາ',
    medSale: 'ຂາຍຢາ',
    lab: 'ຜົນກວດວິເຄາະ',
    revenue: 'ລາຍງານລາຍຮັບ',
    stockReport: 'ລາຍງານຄັງຢາ',
    profitReport: 'ລາຍງານຜົນກຳໄລຢາ',
    settings: 'ຕັ້ງຄ່າ',
  },

  headings: {
    dashboard: 'ແດຊບອດ',
    appointments: 'ນັດໝາຍທັງໝົດ',
    patients: 'ຄົນເຈັບທັງໝົດ',
    billing: 'ໃບເກັບເງິນ',
    inventory: 'ຄັງຢາ ແລະ ອຸປະກອນ',
    medSale: 'ຂາຍຢາ',
    lab: 'ຜົນກວດວິເຄາະ',
    revenue: 'ລາຍງານລາຍຮັບ',
    stockReport: 'ລາຍງານຄັງຢາ',
    profitReport: 'ລາຍງານຜົນກຳໄລຢາ',
    settings: 'ຕັ້ງຄ່າ',
  },

  kpi: {
    today: 'ນັດໝາຍມື້ນີ້',
    patients: 'ຄົນເຈັບທັງໝົດ',
    revenue: 'ລາຍຮັບເດືອນນີ້',
    myRevenue: 'ຍອດຂອງຂ້ອຍເດືອນນີ້',
    myLabTests: 'ຜົນກວດຂອງຂ້ອຍເດືອນນີ້',
    lowstock: 'ຢາໃກ້ໝົດ',
  },

  chartTitle: 'ການເຄື່ອນໄຫວ 14 ວັນຜ່ານມາ',
  chartSub: 'ນັດໝາຍ vs ຄົນເຈັບມາຫາ (ຄົນ)',
  legendA: 'ນັດໝາຍ',
  legendB: 'ມາຫາ',
  donutTitle: 'ສັດສ່ວນລາຍຮັບຕາມບໍລິການ',
  lowStockTitle: 'ຢາໃກ້ໝົດ',
  lowStockSub: 'ລາຍການທີ່ຄວນສັ່ງເພີ່ມ',
  activityTitle: 'ກິດຈະກຳລ່າສຸດ',
  todaySchedule: 'ຕາຕະລາງນັດໝາຍມື້ນີ້',
  recentPatients: 'ຄົນເຈັບຫຼ້າສຸດ',

  revenueSub: 'ພາບລວມລາຍຮັບ 6 ເດືອນຜ່ານມາ',
  exportReport: 'ສົ່ງອອກລາຍງານ',
  avgPerPatient: 'ສະເລ່ຍ/ຄົນເຈັບ',
  monthlyRevenueTitle: 'ລາຍຮັບແຕ່ລະເດືອນ',
  monthlyRevenueSub: 'ກີບ, 6 ເດືອນຜ່ານມາ',
  topInvoicesTitle: 'ໃບເກັບເງິນຫຼ້າສຸດ',
  outstanding: 'ຄ້າງຈ່າຍ',
  totalRevenue: 'ລາຍຮັບທັງໝົດ',
  paidCount: 'ໃບທີ່ຈ່າຍແລ້ວ',

  months: [
    'ມ.ກ.',
    'ກ.ພ.',
    'ມີ.ນ.',
    'ເມ.ສ.',
    'ພ.ພ.',
    'ມິ.ຖ.',
    'ກ.ລ.',
    'ສ.ຫ.',
    'ກ.ຍ.',
    'ຕ.ລ.',
    'ພ.ຈ.',
    'ທ.ວ.',
  ],
  weekdays: ['ອາ', 'ຈ', 'ອ', 'ພ', 'ພຫ', 'ສ', 'ສ'],

  table: {
    patient: 'ຄົນເຈັບ',
    doctor: 'ໝໍ',
    time: 'ເວລາ',
    status: 'ສະຖານະ',
    age: 'ອາຍຸ',
    phone: 'ເບີໂທ',
    lastVisit: 'ມາຫາຫຼ້າສຸດ',
    service: 'ບໍລິການ',
    amount: 'ຈຳນວນເງິນ',
    date: 'ວັນທີ',
    item: 'ລາຍການ',
    category: 'ປະເພດ',
    stock: 'ຄົງເຫຼືອ',
    unitPrice: 'ລາຄາ/ໜ່ວຍ',
    invoice: 'ເລກທີ່',
    actions: '',
    source: 'ແຫຼ່ງທີ່ມາ',
  },

  status: {
    confirmed: 'ຢືນຢັນແລ້ວ',
    pending: 'ລໍຖ້າ',
    arrived: 'ມາຮອດແລ້ວ',
    done: 'ສຳເລັດ',
    cancelled: 'ຍົກເລີກ',
    noShow: 'ບໍ່ມາ',
    paid: 'ຈ່າຍແລ້ວ',
    partial: 'ຈ່າຍບາງສ່ວນ',
    unpaid: 'ຄ້າງຈ່າຍ',
    draft: 'ຮ່າງ',
    void: 'ຍົກເລີກແລ້ວ',
    low: 'ໃກ້ໝົດ',
    ok: 'ພຽງພໍ',
    active: 'ປົກກະຕິ',
    followUp: 'ນັດຕິດຕາມ',
    new: 'ໃໝ່',
  },

  btn: {
    // No leading "+" on any of these: every call site already passes
    // icon="plus" on the Button, so a "+" here doubled it up visually.
    newAppt: 'ນັດໝາຍໃໝ່',
    newApptShort: 'ນັດໝາຍໃໝ່',
    newPatient: 'ຄົນເຈັບໃໝ່',
    newInvoice: 'ໃບເກັບເງິນໃໝ່',
    newLab: 'ຜົນກວດໃໝ່',
    newSale: 'ຂາຍຢາໃໝ່',
    newUser: 'ຜູ້ໃຊ້ໃໝ່',
    addItem: 'ເພີ່ມລາຍການ',
    stockIn: 'ນຳເຂົ້າຢາ',
    save: 'ບັນທຶກ',
    cancel: 'ຍົກເລີກ',
    close: 'ປິດ',
    edit: 'ແກ້ໄຂ',
    delete: 'ລຶບ',
    signIn: 'ເຂົ້າສູ່ລະບົບ',
    logout: 'ອອກຈາກລະບົບ',
    printReceipt: 'ພິມໃບຮັບເງິນ',
    printNow: 'ພິມ',
    exportExcel: 'ສົ່ງອອກ Excel',
    resetPw: 'ຣີເຊັດລະຫັດ',
    retry: 'ລອງໃໝ່',
    scan: 'ຍີງ barcode',
    confirm: 'ຢືນຢັນ',
  },

  login: {
    title: 'ເຂົ້າສູ່ລະບົບ',
    subtitle: 'ລະບົບຄຸ້ມຄອງ ຊີນາລີ ຄລີນິກ',
    username: 'ຊື່ຜູ້ໃຊ້',
    password: 'ລະຫັດຜ່ານ',
    forgot: 'ລືມລະຫັດຜ່ານ?',
    or: 'ຫຼື',
    usePasskey: 'ເຂົ້າດ້ວຍລາຍນີ້ວມື',
    passkeyHint: 'ຕ້ອງລົງທະບຽນອຸປະກອນນີ້ໄວ້ກ່ອນ ທີ່ ຕັ້ງຄ່າ → ສ່ວນຕົວ',
  },

  passkeys: {
    title: 'ເຂົ້າລະບົບດ້ວຍລາຍນີ້ວມື',
    hint: 'ລົງທະບຽນອຸປະກອນນີ້ໄວ້ ເພື່ອເຂົ້າລະບົບດ້ວຍລາຍນີ້ວມື ຫຼື ໜ້າ ແທນການພິມລະຫັດຜ່ານ. ລະຫັດຜ່ານຍັງໃຊ້ໄດ້ຢູ່ສະເໝີ',
    registerThisDevice: 'ລົງທະບຽນອຸປະກອນນີ້',
    deviceLabel: 'ຊື່ອຸປະກອນ',
    deviceLabelHint: 'ເຊັ່ນ: ຄອມພິວເຕີຕ້ອນຮັບ, ມືຖືຂອງໝໍ',
    added: 'ລົງທະບຽນເມື່ອ',
    lastUsed: 'ໃຊ້ຫຼ້າສຸດ',
    unnamed: 'ອຸປະກອນບໍ່ມີຊື່',
    registered: 'ລົງທະບຽນອຸປະກອນແລ້ວ',
    none: 'ຍັງບໍ່ໄດ້ລົງທະບຽນອຸປະກອນໃດ',
    noneSub: 'ລົງທະບຽນແລ້ວຈະເຂົ້າລະບົບດ້ວຍລາຍນີ້ວມືໄດ້ທັນທີ',
    unsupported:
      'ອຸປະກອນ ຫຼື browser ນີ້ບໍ່ຮອງຮັບການເຂົ້າດ້ວຍລາຍນີ້ວມື (ຕ້ອງໃຊ້ຜ່ານ HTTPS ແລະ ມີເຊັນເຊີລາຍນີ້ວມື/ໜ້າ)',
  },

  settings: {
    profile: 'ຂໍ້ມູນສ່ວນຕົວ',
    clinicInfo: 'ຂໍ້ມູນຄລີນິກ',
    language: 'ພາສາ',
    theme: 'ໂທນສີ',
    mode: 'ໂໝດການສະແດງ',
    users: 'ຜູ້ໃຊ້ລະບົບ',
  },

  themes: {
    rose: 'ບົວ',
    red: 'ແດງ',
    berry: 'ບົວເຂັ້ມ',
  },

  modeLight: 'ສະຫວ່າງ',
  modeDark: 'ມືດ',

  // docs/spec.md §6. The prototype used super_admin / doctor / nurse / sales;
  // these four come from the requirements interview and are pending final
  // confirmation before P2.
  roles: {
    admin: 'ຜູ້ຄຸ້ມຄອງລະບົບ',
    doctor: 'ໝໍ',
    reception: 'ພະນັກງານຕ້ອນຮັບ',
    lab: 'ຫ້ອງກວດ',
  },

  userRoleLabel: 'ສິດຜູ້ໃຊ້',
  userActive: 'ນຳໃຊ້ຢູ່',
  userInactive: 'ປິດນຳໃຊ້',
  walkIn: 'ລູກຄ້າ walk-in',
  stockWarn: 'ຄັງເຫຼືອບໍ່ພຽງພໍ',
  saleNo: 'ເລກທີ່ຂາຍ',
  saleReceiptTitle: 'ໃບຂາຍຢາ (Sale receipt)',
  receiptTitle: 'ໃບຮັບເງິນ (Receipt)',
  labReportTitle: 'ຜົນກວດເລືອດ',
  unit: 'ໜ່ວຍ',
  currencyKip: 'ກີບ',

  fields: {
    patientName: 'ຊື່ຄົນເຈັບ',
    doctor: 'ໝໍ',
    date: 'ວັນທີ',
    dob: 'ວັນເດືອນປີເກີດ',
    time: 'ເວລາ',
    type: 'ປະເພດ',
    notes: 'ໝາຍເຫດ',
    service: 'ບໍລິການ',
    amount: 'ຈຳນວນເງິນ',
    itemName: 'ຊື່ລາຍການ',
    qty: 'ຈຳນວນ',
    price: 'ລາຄາ',
    total: 'ລວມ',
    age: 'ອາຍຸ',
    tel: 'ເບີໂທ',
    occupation: 'ອາຊີບ',
    address: 'ບ່ອນຢູ່',
    diagnosis: 'ມະຕິພະຍາດ',
    extNo: 'ເລກທີ',
    itemsTitle: 'ລາຍການ',
    addItem: 'ເພີ່ມແຖວ',
    cashier: 'ຜູ້ຮັບເງິນ',
    treatingDoctor: 'ແພດປິ່ນປົວ',
    labNo: 'Lab No',
    extLabNo: 'Ext Lab No',
    extHn: 'Ext HN',
    ward: 'Ward',
    gender: 'ເພດ',
    analyte: 'ລາຍການກວດ',
    results: 'ຜົນກວດ',
    unit: 'ໜ່ວຍ',
    range: 'ຄ່າປົກກະຕິ',
    flag: 'ໝາຍເຫດ',
    reportedBy: 'ຜູ້ລາຍງານ',
    approveBy: 'ຮັບຮອງໂດຍ',
    expiry: 'ວັນໝົດອາຍຸ',
    barcode: 'ລະຫັດຢາ / Barcode',
    barcodeScan: 'ພິມ ຫຼື ຍີງ barcode ແລ້ວກົດ Enter',
    staff: 'ພະນັກງານ',
    currency: 'ສະກຸນເງິນ',
  },

  currencies: {
    LAK: 'ກີບ (LAK)',
    THB: 'ບາດ (THB)',
    USD: 'ໂດລາ (USD)',
  },

  gender: {
    female: 'ຍິງ',
    male: 'ຊາຍ',
    other: 'ອື່ນໆ',
  },

  patients: {
    searchPlaceholder: 'ຄົ້ນຫາຊື່, ເບີໂທ, ລະຫັດ ຫຼື Ext HN',
    code: 'ລະຫັດ',
    noResults: 'ບໍ່ພົບຄົນເຈັບທີ່ຄົ້ນຫາ',
    detailTitle: 'ຂໍ້ມູນຄົນເຈັບ',
    tabInfo: 'ຂໍ້ມູນທົ່ວໄປ',
    tabAppointments: 'ນັດໝາຍ',
    tabInvoices: 'ໃບເກັບເງິນ',
    tabLab: 'ຜົນກວດ',
    comingInPhase: 'ຈະມາໃນຂັ້ນຕອນຕໍ່ໄປ',
    editTitle: 'ແກ້ໄຂຂໍ້ມູນຄົນເຈັບ',
    dobOrAge: 'ໃສ່ວັນເກີດ ຫຼື ອາຍຸ ຢ່າງໜ້ອຍ 1 ຢ່າງ',
    extHnHint: 'ລະຫັດຈາກລະບົບເກົ່າ (ຖ້າມີ)',
    wardHint: 'ພະແນກ/ຄອມຈາກໂຮງໝໍທີ່ສົ່ງມາ (ຖ້າມີ)',
    saved: 'ບັນທຶກແລ້ວ',
    bloodType: 'ກຣຸບເລືອດ',
    registeredAt: 'ວັນທີລົງທະບຽນ',
    cannotDelete: 'ບໍ່ສາມາດລຶບໄດ້',
    cannotDeleteHasRecords: 'ຄົນເຈັບລາຍນີ້ມີໃບເກັບເງິນ ຫຼື ຜົນກວດຢູ່ໃນລະບົບ ຈຶ່ງບໍ່ອະນຸຍາດໃຫ້ລຶບ',
  },

  appointments: {
    bookTitle: 'ຈອງນັດໝາຍ',
    duration: 'ໄລຍະເວລາ (ນາທີ)',
    noneToday: 'ບໍ່ມີນັດໝາຍ',
    conflictTitle: 'ໝໍມີນັດຊ້ອນກັນ',
    conflictBody: 'ຊ່ວງເວລານີ້ໝໍມີນັດຢູ່ແລ້ວ ຕ້ອງການຈອງຊ້ອນກັນບໍ?',
    bookAnyway: 'ຈອງຊ້ອນກັນ',
    selectDoctor: 'ເລືອກໝໍ',
    noDoctor: 'ຍັງບໍ່ກຳນົດ',
    changeStatus: 'ປ່ຽນສະຖານະ',
    booked: 'ຈອງນັດແລ້ວ',
  },

  scanner: {
    needsHttps:
      'ກ້ອງໃຊ້ໄດ້ສະເພາະຜ່ານ HTTPS. ພິມລະຫັດດ້ວຍມືແທນ ຫຼື ຕິດຕໍ່ຜູ້ດູແລລະບົບ (ເບິ່ງ docs/setup.md ຂໍ້ 5)',
    permissionDenied: 'ບໍ່ໄດ້ຮັບອະນຸຍາດໃຫ້ໃຊ້ກ້ອງ — ພິມລະຫັດດ້ວຍມືແທນ',
    cameraFailed: 'ເປີດກ້ອງບໍ່ໄດ້ — ພິມລະຫັດດ້ວຍມືແທນ',
  },

  inventory: {
    searchPlaceholder: 'ຄົ້ນຫາຊື່ຢາ, ລະຫັດ ຫຼື barcode',
    lowOnly: 'ສະເພາະທີ່ໃກ້ໝົດ',
    stockIn: 'ນຳເຂົ້າຢາ',
    stockInQty: 'ຈຳນວນທີ່ນຳເຂົ້າ',
    unitCost: 'ລາຄາຕົ້ນທຶນ/ໜ່ວຍ',
    newExpiry: 'ວັນໝົດອາຍຸໃໝ່ (ຖ້າມີ)',
    reorderLevel: 'ລະດັບເຕືອນສັ່ງເພີ່ມ',
    sellPrice: 'ລາຄາຂາຍ',
    costPrice: 'ລາຄາຕົ້ນທຶນ',
    stockOk: 'ພຽງພໍ',
    stockLow: 'ໃກ້ໝົດ',
    stockOut: 'ໝົດ',
    expirySoon: 'ໃກ້ໝົດອາຍຸ',
    expired: 'ໝົດອາຍຸແລ້ວ',
    noItems: 'ຍັງບໍ່ມີລາຍການໃນຄັງ',
    savedStockIn: 'ນຳເຂົ້າແລ້ວ',
    categories: 'ປະເພດຢາ ແລະ ອຸປະກອນ',
    categoriesHint: 'ລາຍການນີ້ຈະຂຶ້ນໃຫ້ເລືອກຕອນເພີ່ມ ຫຼື ແກ້ໄຂຢາ',
    categoryHint: 'ຈັດການລາຍການໄດ້ທີ່ ຕັ້ງຄ່າ → ປະເພດຢາ',
    newCategory: 'ເພີ່ມປະເພດ',
    noCategories: 'ຍັງບໍ່ມີປະເພດ — ເພີ່ມກ່ອນຈຶ່ງເລືອກໄດ້ຕອນເພີ່ມຢາ',
    categoryTaken: 'ປະເພດນີ້ມີແລ້ວ',
    categoryInUse: 'ລຶບບໍ່ໄດ້ເພາະຍັງມີຢາໃຊ້ປະເພດນີ້ຢູ່ — ໃຫ້ປິດນຳໃຊ້ແທນ',
    renameWarning: 'ປ່ຽນຊື່ນີ້ຈະປ່ຽນໃຫ້ຢາ {n} ລາຍການທີ່ໃຊ້ປະເພດນີ້ນຳ',
    sortOrder: 'ລຳດັບ',
    itemsUsing: 'ຈຳນວນຢາ',
    sellBelowCostWarning: 'ລາຄາຂາຍຕ່ຳກວ່າລາຄາຕົ້ນທຶນ — ອາດຂາຍຂາດທຶນ',
    movementHistory: 'ປະຫວັດການເຄື່ອນໄຫວ',
    movementType: 'ປະເພດການເຄື່ອນໄຫວ',
    movementTypes: {
      in: 'ນຳເຂົ້າ',
      out: 'ຕັດອອກ',
      adjust: 'ປັບປຸງ',
      sale: 'ຂາຍ',
      return: 'ຄືນສະຕັອກ',
    },
    received: 'ຮັບເຂົ້າ',
    sold: 'ຂາຍອອກ',
    returned: 'ຄືນສະຕັອກ',
    adjusted: 'ປັບປຸງ',
    writtenOff: 'ຕັດອອກ',
    netChange: 'ການປ່ຽນແປງສຸດທິ',
  },

  pharmacy: {
    wholesale: 'ຂາຍສົ່ງ',
    wholesaleHint: '(ແກ້ໄຂລາຄາໃນຕາຕະລາງໄດ້)',
    cart: 'ຕະກຣ້າ',
    cartEmpty: 'ຍັງບໍ່ມີລາຍການ — ຍີງ ຫຼື ພິມ barcode ເພື່ອເພີ່ມ',
    customer: 'ລູກຄ້າ',
    checkout: 'ຄິດເງິນ',
    grandTotal: 'ລວມທັງໝົດ',
    notEnough: 'ຄັງບໍ່ພຽງພໍ',
    notFound: 'ບໍ່ພົບລາຍການທີ່ກົງກັບລະຫັດນີ້',
    sold: 'ຂາຍສຳເລັດ',
    history: 'ປະຫວັດການຂາຍ',
    available: 'ຄົງເຫຼືອ',
    paymentMethod: 'ຊຳລະດ້ວຍ',
    remove: 'ເອົາອອກ',
    voidTitle: 'ຍົກເລີກການຂາຍ',
    voidReason: 'ເຫດຜົນທີ່ຍົກເລີກ',
    voidWarning: 'ການຂາຍທີ່ຍົກເລີກຈະຄືນສະຕັອກຢາທັງໝົດ ແລະ ບໍ່ສາມາດແກ້ຄືນໄດ້',
    voided: 'ຍົກເລີກການຂາຍແລ້ວ',
  },

  payment: {
    cash: 'ເງິນສົດ',
    transfer: 'ໂອນ',
    card: 'ບັດ',
  },

  print: {
    backgroundHint: 'ຖ້າເສັ້ນຕາຕະລາງບໍ່ຂຶ້ນ ໃຫ້ເປີດ "Background graphics" ໃນໜ້າຕັ້ງຄ່າການພິມ',
  },

  invoices: {
    searchPlaceholder: 'ຄົ້ນຫາເລກທີ, Ext No ຫຼື ຊື່ຄົນເຈັບ',
    newTitle: 'ອອກໃບເກັບເງິນ',
    discount: 'ສ່ວນຫຼຸດ',
    subtotal: 'ລວມຍ່ອຍ',
    outstanding: 'ຄ້າງຈ່າຍ',
    addLine: 'ເພີ່ມແຖວ',
    fromService: 'ຈາກບໍລິການ',
    fromDrug: 'ຈາກຄັງຢາ',
    pricePerLine: 'ລາຄາ/ລາຍການ',
    custom: 'ພິມເອງ',
    takePayment: 'ຮັບຊຳລະ',
    paymentAmount: 'ຈຳນວນທີ່ຮັບ',
    payFull: 'ຈ່າຍເຕັມ',
    voidTitle: 'ຍົກເລີກໃບເກັບເງິນ',
    voidReason: 'ເຫດຜົນທີ່ຍົກເລີກ',
    voidWarning: 'ໃບທີ່ຍົກເລີກແລ້ວຈະຮັບເງິນບໍ່ໄດ້ອີກ ແຕ່ຍັງເກັບໄວ້ໃນລະບົບ',
    noInvoices: 'ຍັງບໍ່ມີໃບເກັບເງິນ',
    saved: 'ອອກໃບເກັບເງິນແລ້ວ',
    paymentSaved: 'ຮັບຊຳລະແລ້ວ',
    voided: 'ຍົກເລີກແລ້ວ',
    exceedsOutstanding: 'ຈຳນວນເກີນຍອດຄ້າງຈ່າຍ',
  },

  services: {
    title: 'ບໍລິການ',
    hint: 'ລາຍການບໍລິການ ແລະ ລາຄາເລີ່ມຕົ້ນທີ່ຈະຂຶ້ນໃຫ້ເລືອກຕອນອອກໃບເກັບເງິນ',
    newService: 'ບໍລິການໃໝ່',
    code: 'ລະຫັດ',
    name: 'ຊື່ບໍລິການ',
    taken: 'ລະຫັດນີ້ມີແລ້ວ',
    noServices: 'ຍັງບໍ່ມີບໍລິການ',
  },

  lab: {
    newOrder: 'ສັ່ງກວດໃໝ່',
    selectTests: 'ເລືອກລາຍການກວດ',
    selectedCount: 'ເລືອກແລ້ວ',
    noneSelected: 'ຍັງບໍ່ໄດ້ເລືອກລາຍການກວດ',
    enterResults: 'ປ້ອນຜົນກວດ',
    hasValue: 'ມີຜົນແລ້ວ',
    removeWithValue: 'ລາຍການນີ້ມີຜົນກວດແລ້ວ ຖອນຕິກຈະລຶບຜົນອອກ ຕ້ອງການສືບຕໍ່ບໍ?',
    approve: 'ຮັບຮອງຜົນ',
    unapprove: 'ຖອນການຮັບຮອງ',
    approved: 'ຮັບຮອງແລ້ວ',
    approvedLocked: 'ຮັບຮອງແລ້ວ — ແກ້ໄຂບໍ່ໄດ້',
    extLabNo: 'Ext Lab No (ຈາກຫ້ອງແລັບພາຍນອກ)',
    noOrders: 'ຍັງບໍ່ມີລາຍການກວດ',
    saved: 'ບັນທຶກຜົນກວດແລ້ວ',
    orderCreated: 'ສັ່ງກວດແລ້ວ',
    searchPlaceholder: 'ຄົ້ນຫາ Lab No',
    reportTitle: 'ຜົນກວດເລືອດ',
  },

  reports: {
    range: 'ຊ່ວງວັນທີ',
    thisMonth: 'ເດືອນນີ້',
    last3Months: '3 ເດືອນ',
    last6Months: '6 ເດືອນ',
    thisYear: 'ປີນີ້',
    billed: 'ອອກໃບແລ້ວ',
    collected: 'ຮັບເງິນແລ້ວ',
    patientsBilled: 'ຄົນເຈັບທີ່ອອກໃບ',
    serviceMixNote: 'ຄິດຈາກຍອດທີ່ອອກໃບ ບໍ່ແມ່ນຍອດທີ່ຮັບເງິນແລ້ວ',
    noData: 'ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້',
    totalLabel: 'ລວມ',
    totalCost: 'ຕົ້ນທຶນລວມ',
    totalProfit: 'ກຳໄລລວມ',
    profitMargin: 'ອັດຕາກຳໄລ',
    sourceInvoice: 'ໃບເກັບເງິນ',
    sourceSale: 'ຂາຍຢາ',
    cost: 'ຕົ້ນທຶນ',
    profit: 'ກຳໄລ',
    profitHint: 'ຄິດຈາກລາຄາຕົ້ນທຶນປັດຈຸບັນຂອງແຕ່ລະລາຍການ ບໍ່ແມ່ນລາຄາຕົ້ນທຶນຕອນຂາຍ',
  },

  settingsPage: {
    tabPersonal: 'ສ່ວນຕົວ',
    tabClinic: 'ຂໍ້ມູນຄລີນິກ',
    tabSystem: 'ຄ່າລະບົບ',
    tabUsers: 'ຜູ້ໃຊ້ລະບົບ',
    tabCategories: 'ປະເພດຢາ',
    tabServices: 'ບໍລິການ',
    tabMenuAccess: 'ສິດເມນູ',
    menuAccessHint:
      'ເລືອກວ່າແຕ່ລະສິດຜູ້ໃຊ້ຈະເຫັນເມນູໃດແດ່ ໃນແຖບຂ້າງ. ນີ້ຄວບຄຸມແຕ່ການສະແດງເມນູ — ສິດເຂົ້າເຖິງຂໍ້ມູນຈິງຍັງເປັນໄປຕາມການອອກແບບເດີມ',
    logo: 'ໂລໂກ້ຄລີນິກ',
    logoHint: 'ໄຟລ໌ຮູບບໍ່ເກີນ 512 KB — ໃຊ້ໃນໜ້າຈໍ ແລະ ຫົວໃບພິມ',
    logoTooLarge: 'ໄຟລ໌ໃຫຍ່ເກີນ 512 KB',
    roundingUnit: 'ປັດເສດຍອດລວມ (ກີບ)',
    expiryWarnDays: 'ເຕືອນກ່ອນໝົດອາຍຸ (ວັນ)',
    fxThb: 'ອັດຕາແລກປ່ຽນ ບາດ (ກີບ/1 ບາດ)',
    fxUsd: 'ອັດຕາແລກປ່ຽນ ໂດລາ (ກີບ/1 ໂດລາ)',
    fxHint: 'ໃຊ້ຄິດຄາງວດເປັນບາດ/ໂດລາ ໃນໜ້າໃບເກັບເງິນ ແລະ ຂາຍຢາ — ຍອດຈິງໃນລະບົບຍັງເປັນກີບສະເໝີ',
    sessionTtlHours: 'ໝົດອາຍຸ Session (ຊົ່ວໂມງ)',
    sessionTtlHint: 'ອອກຈາກລະບົບອັດຕະໂນມັດຖ້າບໍ່ມີການນຳໃຊ້ດົນເທົ່ານີ້ (ຄ່າເລີ່ມຕົ້ນ 24 ຊົ່ວໂມງ)',
    sessionExpiredTitle: 'ໝົດເວລາການນຳໃຊ້',
    sessionExpiredBody:
      'ບໍ່ພົບການນຳໃຊ້ດົນເກີນໄປ ຫຼືການເຂົ້າສູ່ລະບົບຖືກຍົກເລີກ — ກະລຸນາເຂົ້າສູ່ລະບົບໃໝ່',
    defaultLang: 'ພາສາເລີ່ມຕົ້ນ',
    defaultTheme: 'ໂທນສີເລີ່ມຕົ້ນ',
    defaultMode: 'ໂໝດເລີ່ມຕົ້ນ',
    changePassword: 'ປ່ຽນລະຫັດຜ່ານ',
    currentPassword: 'ລະຫັດຜ່ານປັດຈຸບັນ',
    newPassword: 'ລະຫັດຜ່ານໃໝ່',
    passwordMin: 'ຢ່າງໜ້ອຍ 10 ຕົວອັກສອນ',
    passwordChanged: 'ປ່ຽນລະຫັດຜ່ານແລ້ວ',
    passwordWrong: 'ລະຫັດຜ່ານປັດຈຸບັນບໍ່ຖືກຕ້ອງ',
    newUser: 'ເພີ່ມຜູ້ໃຊ້',
    username: 'ຊື່ຜູ້ໃຊ້',
    fullName: 'ຊື່ເຕັມ',
    resetPassword: 'ຣີເຊັດລະຫັດຜ່ານ',
    resetWarning: 'ຜູ້ໃຊ້ຄົນນີ້ຈະຖືກໄລ່ອອກຈາກທຸກເຄື່ອງທັນທີ',
    deactivate: 'ປິດນຳໃຊ້',
    activate: 'ເປີດນຳໃຊ້',
    lastLogin: 'ເຂົ້າລ່າສຸດ',
    usernameTaken: 'ຊື່ຜູ້ໃຊ້ນີ້ມີແລ້ວ',
    lastAdmin: 'ຕ້ອງເຫຼືອຜູ້ຄຸ້ມຄອງລະບົບຢ່າງໜ້ອຍ 1 ຄົນ',
    selfDeactivate: 'ປິດບັນຊີຕົນເອງບໍ່ໄດ້',
    saved: 'ບັນທຶກແລ້ວ',
    adminOnly: 'ສະເພາະຜູ້ຄຸ້ມຄອງລະບົບ',
  },

  common: {
    loading: 'ກຳລັງໂຫຼດ...',
    error: 'ເກີດຂໍ້ຜິດພາດ',
    noData: 'ຍັງບໍ່ມີຂໍ້ມູນ',
    noDataSub: 'ເມື່ອມີຂໍ້ມູນແລ້ວ ຈະສະແດງຢູ່ບ່ອນນີ້',
    confirmDiscard: 'ຂໍ້ມູນທີ່ປ້ອນຍັງບໍ່ໄດ້ບັນທຶກ. ຕ້ອງການປິດຫຼືບໍ່?',
    confirmStatusChange: 'ຕ້ອງການປ່ຽນສະຖານະນີ້ບໍ?',
    confirmDelete: 'ຕ້ອງການລຶບລາຍການນີ້ບໍ? ຈະກູ້ຄືນບໍ່ໄດ້',
    confirmVoid: 'ຢືນຢັນຍົກເລີກ',
    deleted: 'ລຶບແລ້ວ',
    of: 'ຈາກ',
    page: 'ໜ້າ',
    prev: 'ກ່ອນໜ້າ',
    next: 'ຖັດໄປ',
    notFound: 'ບໍ່ພົບໜ້ານີ້',
    backHome: 'ກັບໜ້າຫຼັກ',
    offline: 'ຂາດການເຊື່ອມຕໍ່ເຄືອຂ່າຍ — ບັນທຶກຂໍ້ມູນບໍ່ໄດ້ຈົນກວ່າຈະຕໍ່ຄືນ',
    sessionExpired: 'ໝົດເວລາເຂົ້າໃຊ້ງານ ກະລຸນາເຂົ້າສູ່ລະບົບໃໝ່',
  },

  errors: {
    UNAUTHENTICATED: 'ກະລຸນາເຂົ້າສູ່ລະບົບໃໝ່',
    INVALID_CREDENTIALS: 'ຊື່ຜູ້ໃຊ້ ຫຼື ລະຫັດຜ່ານບໍ່ຖືກຕ້ອງ',
    FORBIDDEN: 'ທ່ານບໍ່ມີສິດເຂົ້າເຖິງສ່ວນນີ້',
    NOT_FOUND: 'ບໍ່ພົບຂໍ້ມູນທີ່ຕ້ອງການ',
    VALIDATION_FAILED: 'ຂໍ້ມູນທີ່ປ້ອນບໍ່ຖືກຕ້ອງ',
    CONFLICT: 'ຂໍ້ມູນຂັດແຍ້ງກັນ ກະລຸນາໂຫຼດຄືນ',
    INSUFFICIENT_STOCK: 'ຄັງເຫຼືອບໍ່ພຽງພໍ',
    RATE_LIMITED: 'ພະຍາຍາມຫຼາຍເກີນໄປ ກະລຸນາລໍຖ້າ',
    INTERNAL: 'ລະບົບຂັດຂ້ອງ ກະລຸນາລອງໃໝ່',
  },
} as const;
